import * as React from 'react';
import {
    buildColumns,
    CheckboxVisibility,
    ColumnActionsMode,
    DetailsList,
    IColumn,
    IDetailsRowProps,
    IObjectWithKey,
    ISelection,
    Selection,
    SelectionMode,
} from 'office-ui-fabric-react/lib/DetailsList';
import { ControlIcons } from 'owa-control-icons';
import { FolderRowData } from '../types/FolderRowData';
import { FolderSelectorRow, getCustomRowKeysMap } from './Row/FolderSelectorRow';
import { getAllFolderRows } from '../selectors/folderRowSelectors';
import { getFileSpriteIconClass } from 'owa-file-icon';
import { getFolderSelectorColumnInfo } from '../selectors/columnInfoSelectors';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ICON_PROPERTY_NAME, NAME_PROPERTY_NAME } from '../constants';
import { observer } from 'mobx-react';
import { observer as observerLite } from 'mobx-react-lite';

const styles = require('./FolderSelector.scss');
const classNames = require('classnames');

const KeyColumnName = 'key';

const ROW_WIDTH = 328; // The width of the row is 328px;

export interface FolderSelectorProps {
    onFolderSelected: (selectedFolderKey: string) => void;
}

export interface FolderSelectorState {
    // The selection model is used to track the selection state
    selection: ISelection;
}

@observer
export class FolderSelector extends React.Component<FolderSelectorProps, FolderSelectorState> {
    constructor(props: FolderSelectorProps) {
        super(props);

        this.state = {
            selection: new Selection({}),
        };
    }

    render() {
        const folderRows = getAllFolderRows();

        // If there are no folders then we get
        // the default columns
        const columns = folderRows.length
            ? this.buildColumnsForData(folderRows)
            : this.getDefaultColumns();

        // We set the viewport width to row width so that
        // the size of row is correct. As the actuall view port
        // (which is the container containing details list) is
        // bigger than the content but we don't want the content
        // to exceed a certain size hence we set the view port
        // width which is used to determine the row width.
        return (
            <DetailsList
                viewport={{ width: ROW_WIDTH, height: 0 }}
                isHeaderVisible={false}
                items={folderRows}
                columns={columns}
                onRenderRow={this.onRenderRow}
                onRenderItemColumn={this.onRenderItemColumn}
                selectionMode={SelectionMode.none}
                selection={this.state.selection}
                onItemInvoked={this.onItemSelected}
                checkboxVisibility={CheckboxVisibility.hidden}
            />
        );
    }

    private getDefaultColumns(): IColumn[] {
        const defaultItem: FolderRowData = {
            key: '',
            icon: ControlIcons.FabricFolder,
            name: '',
        };

        return this.buildColumnsForData([defaultItem]);
    }

    private buildColumnsForData(items: FolderRowData[]): IColumn[] {
        const columns = buildColumns(items);

        columns.forEach(column => {
            const { key } = column;
            const columnInfo = getFolderSelectorColumnInfo(key);

            if (columnInfo) {
                const { name, minWidth, maxWidth } = columnInfo;
                column.name = name;
                column.minWidth = minWidth;
                column.maxWidth = maxWidth;
            }

            if (column.key === ICON_PROPERTY_NAME) {
                column.iconClassName = classNames(
                    getFileSpriteIconClass('generic_16x16'),
                    styles.iconColumnHeaderIcon
                );
                column.headerClassName = styles.iconColumnHeader;
                column.columnActionsMode = ColumnActionsMode.disabled;
            }
        });

        return columns.filter(column => column.key !== KeyColumnName);
    }

    private onRenderRow = (props: IDetailsRowProps) => {
        return <FolderRow detailsRowProps={props} onFolderSelected={this.props.onFolderSelected} />;
    };

    private onRenderItemColumn(item: FolderRowData, index: number, column: IColumn): JSX.Element {
        switch (column.key) {
            case ICON_PROPERTY_NAME:
                const icon: ControlIcons = item[column.fieldName];
                return (
                    <div className={styles.itemIconContainer}>
                        <Icon
                            className={styles.itemIcon}
                            iconName={icon}
                            data-is-focusable={false}
                        />
                    </div>
                );
            case NAME_PROPERTY_NAME:
                const nameFieldContent: string = item[column.fieldName];
                return <span className={styles.rowNameCell}>{nameFieldContent}</span>;
            default:
                const fieldContent: string = item[column.fieldName];
                return <span>{fieldContent}</span>;
        }
    }

    private onItemSelected = (item: IObjectWithKey) => {
        onFolderItemSelected(item, this.props.onFolderSelected);
    };
}

function onFolderItemSelected(item: IObjectWithKey, onFolderSelected: (folderId: string) => void) {
    const customRowKeysMap = getCustomRowKeysMap();

    if (!customRowKeysMap[item.key]) {
        // Only trigger folder selected when the selected
        // item is not a custom row
        onFolderSelected(item.key as string);
    }
}

interface FolderRowProps {
    detailsRowProps: IDetailsRowProps;
    onFolderSelected: (folderId: string) => void;
}

const FolderRow = observerLite(function FolderRow(props: FolderRowProps) {
    const { detailsRowProps, onFolderSelected } = props;

    const onItemSelected = React.useCallback(() => {
        const item = detailsRowProps.item;
        onFolderItemSelected(item, onFolderSelected);
    }, [detailsRowProps.item]);

    return (
        <div onClick={onItemSelected}>
            <FolderSelectorRow {...detailsRowProps} className={styles.folderRow} />
        </div>
    );
});
